import React, { useState, useEffect } from 'react';
import { ShoppingCart, Plus, Check, Heart, Eye, ChevronLeft, ChevronRight, Users, TrendingUp } from 'lucide-react';
import { aiApi } from '../services/api';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { Card, CardContent } from './ui/card';

// Product Mini Card
const ProductMiniCard = ({ product, onAddToCart, onQuickView, onWishlist, isInWishlist, compact = false }) => {
  const [added, setAdded] = useState(false);
  const discount = product.original_price 
    ? Math.round((1 - product.price / product.original_price) * 100) 
    : product.discount;

  const handleAddToCart = () => {
    onAddToCart?.(product);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  if (compact) {
    return (
      <div className="flex items-center gap-3 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
        <img
          src={product.image || '/placeholder.png'}
          alt={product.name}
          className="w-12 h-12 rounded-lg object-cover"
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-800 dark:text-white truncate">{product.name}</p>
          <p className="text-sm font-bold text-teal-600">${product.price}</p>
        </div>
        <Button
          size="sm"
          variant={added ? "secondary" : "default"}
          className="h-8 w-8 p-0"
          onClick={handleAddToCart}
        >
          {added ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
        </Button>
      </div>
    );
  }

  return (
    <div className="group relative bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden hover:shadow-lg transition-all duration-300 flex-shrink-0 w-44">
      {/* Discount Badge */}
      {discount > 0 && (
        <Badge className="absolute top-2 left-2 z-10 bg-red-500 text-white text-xs">
          -{discount}%
        </Badge>
      )}

      {/* Wishlist Button */}
      <button
        onClick={() => onWishlist?.(product)}
        className={`absolute top-2 right-2 z-10 p-1.5 rounded-full transition-all ${
          isInWishlist 
            ? 'bg-red-500 text-white' 
            : 'bg-white/80 dark:bg-gray-700/80 text-gray-600 dark:text-gray-300 opacity-0 group-hover:opacity-100'
        }`}
      >
        <Heart className={`w-3 h-3 ${isInWishlist ? 'fill-current' : ''}`} />
      </button>

      {/* Image */}
      <div className="aspect-square bg-gray-100 dark:bg-gray-700 relative overflow-hidden">
        <img
          src={product.image || '/placeholder.png'}
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        
        {/* Quick View Overlay */}
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <Button
            size="sm"
            variant="secondary"
            className="h-8 text-xs"
            onClick={() => onQuickView?.(product)}
          >
            <Eye className="w-3 h-3 mr-1" /> View
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="p-3">
        <p className="text-xs text-gray-500 mb-1">{product.category || product.brand}</p>
        <h3 className="font-medium text-sm text-gray-800 dark:text-white line-clamp-2 min-h-[2.5rem] mb-2">
          {product.name}
        </h3>
        
        {/* Price & Add Button */}
        <div className="flex items-center justify-between">
          <div>
            <span className="font-bold text-teal-600">${product.price}</span>
            {product.original_price && product.original_price > product.price && (
              <span className="text-xs text-gray-400 line-through ml-1">${product.original_price}</span>
            )}
          </div>
          <Button
            size="sm"
            variant={added ? "secondary" : "default"}
            className="h-7 w-7 p-0"
            onClick={handleAddToCart}
          >
            {added ? <Check className="h-3 w-3" /> : <ShoppingCart className="h-3 w-3" />}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Frequently Bought Together Bundle
export const FrequentlyBoughtTogether = ({ productId, onAddToCart, onQuickView }) => {
  const [bundleData, setBundleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedProducts, setSelectedProducts] = useState(new Set());

  useEffect(() => {
    const fetchBundle = async () => {
      if (!productId) return;
      
      try {
        const data = await aiApi.getFrequentlyBoughtTogether(productId);
        setBundleData(data);
        // Pre-select all products
        const allIds = new Set([data.main_product?.id, ...(data.bundle_products || []).map(p => p.id)]);
        setSelectedProducts(allIds);
      } catch (error) {
        console.error('Failed to fetch bundle:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBundle();
  }, [productId]);

  const toggleProduct = (productId) => {
    setSelectedProducts(prev => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  };

  const handleAddBundle = () => {
    if (!bundleData) return;
    
    const allProducts = [bundleData.main_product, ...(bundleData.bundle_products || [])];
    allProducts
      .filter(p => selectedProducts.has(p.id))
      .forEach(product => onAddToCart?.(product));
  };

  const calculateSelectedPrice = () => {
    if (!bundleData) return 0;
    const allProducts = [bundleData.main_product, ...(bundleData.bundle_products || [])];
    return allProducts
      .filter(p => selectedProducts.has(p.id))
      .reduce((sum, p) => sum + (p.price || 0), 0);
  };

  if (loading) {
    return (
      <Card className="p-4">
        <Skeleton className="h-6 w-48 mb-4" />
        <div className="flex gap-4">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="w-24 h-32" />
          ))}
        </div>
      </Card>
    );
  }

  if (!bundleData || !bundleData.bundle_products?.length) return null;

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-4">
          <Users className="w-5 h-5 text-teal-600" />
          <h3 className="font-bold text-gray-800 dark:text-white">Frequently Bought Together</h3>
        </div>

        {/* Products */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          {/* Main Product */}
          <div 
            className={`relative cursor-pointer ${selectedProducts.has(bundleData.main_product.id) ? 'ring-2 ring-teal-500 rounded-lg' : ''}`}
            onClick={() => toggleProduct(bundleData.main_product.id)}
          >
            <img
              src={bundleData.main_product.image || '/placeholder.png'}
              alt={bundleData.main_product.name}
              className="w-20 h-20 rounded-lg object-cover"
            />
            {selectedProducts.has(bundleData.main_product.id) && (
              <div className="absolute -top-1 -right-1 w-5 h-5 bg-teal-500 rounded-full flex items-center justify-center">
                <Check className="w-3 h-3 text-white" />
              </div>
            )}
          </div>

          {bundleData.bundle_products.map((product, idx) => (
            <React.Fragment key={product.id}>
              <Plus className="w-4 h-4 text-gray-400" />
              <div 
                className={`relative cursor-pointer ${selectedProducts.has(product.id) ? 'ring-2 ring-teal-500 rounded-lg' : ''}`}
                onClick={() => toggleProduct(product.id)}
              >
                <img
                  src={product.image || '/placeholder.png'}
                  alt={product.name}
                  className="w-20 h-20 rounded-lg object-cover"
                />
                {selectedProducts.has(product.id) && (
                  <div className="absolute -top-1 -right-1 w-5 h-5 bg-teal-500 rounded-full flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                )}
              </div>
            </React.Fragment>
          ))}
        </div>

        {/* Price Summary */}
        <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
          <div>
            <p className="text-sm text-gray-500">Bundle Price</p>
            <div className="flex items-center gap-2">
              <span className="text-xl font-bold text-teal-600">
                ${(calculateSelectedPrice() * 0.95).toFixed(2)}
              </span>
              <span className="text-sm text-gray-400 line-through">
                ${calculateSelectedPrice().toFixed(2)}
              </span>
              <Badge className="bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
                Save 5%
              </Badge>
            </div>
          </div>
          <Button onClick={handleAddBundle} disabled={selectedProducts.size === 0}>
            <ShoppingCart className="w-4 h-4 mr-2" />
            Add {selectedProducts.size} Items
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Customers Also Bought Carousel
const CustomersAlsoBought = ({ 
  productId, 
  onAddToCart, 
  onQuickView, 
  onWishlist, 
  wishlistItems = [],
  title = "Customers Also Bought",
  limit = 8
}) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollRef = React.useRef(null);

  useEffect(() => {
    const fetchProducts = async () => {
      if (!productId) {
        setLoading(false);
        return;
      }
      
      try {
        const data = await aiApi.getCustomersAlsoBought(productId, limit);
        setProducts(data.products || []);
      } catch (error) {
        console.error('Failed to fetch recommendations:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [productId, limit]);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = direction === 'left' ? -300 : 300;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <div className="py-6">
        <div className="flex items-center gap-2 mb-4">
          <Skeleton className="h-5 w-5 rounded" />
          <Skeleton className="h-6 w-48" />
        </div>
        <div className="flex gap-4 overflow-hidden">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="flex-shrink-0 w-44">
              <Skeleton className="aspect-square rounded-t-xl" />
              <div className="p-3 space-y-2">
                <Skeleton className="h-3 w-16" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-20" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!products.length) return null;

  return (
    <div className="py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-teal-600" />
          <h2 className="text-lg font-bold text-gray-800 dark:text-white">{title}</h2>
        </div>
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => scroll('left')}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => scroll('right')}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Products Carousel */}
      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto scrollbar-hide pb-2 -mx-2 px-2"
        style={{ scrollSnapType: 'x mandatory' }}
      >
        {products.map((product, idx) => (
          <div key={product.id || idx} style={{ scrollSnapAlign: 'start' }}>
            <ProductMiniCard
              product={product}
              onAddToCart={onAddToCart}
              onQuickView={onQuickView}
              onWishlist={onWishlist}
              isInWishlist={wishlistItems.some(w => w.product_id === product.id || w.id === product.id)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default CustomersAlsoBought;
